// Stripe integration service for subscription management
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from '../config/supabase';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export interface CreateCheckoutSessionParams {
  priceId: string;
  userId: string;
  userEmail: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface SubscriptionStatus {
  isActive: boolean;
  status: string;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
}

export class StripeService {
  private static stripe: Stripe | null = null;

  static async getStripe(): Promise<Stripe | null> {
    if (!this.stripe) {
      this.stripe = await stripePromise;
    }
    return this.stripe;
  }

  /**
   * Create a Stripe Checkout session for subscription
   */
  static async createCheckoutSession(params: CreateCheckoutSessionParams): Promise<string> {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId: params.priceId,
        userId: params.userId,
        userEmail: params.userEmail,
        successUrl: params.successUrl || `${window.location.origin}/dashboard?success=true`,
        cancelUrl: params.cancelUrl || `${window.location.origin}/pricing?canceled=true`
      }
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating checkout session:', error);
      throw new Error('Failed to create checkout session');
    }

    return data.url;
  }

  /**
   * Create a Stripe Customer Portal session for subscription management
   */
  static async createPortalSession(customerId: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: {
        customerId,
        returnUrl: window.location.origin
      }
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating portal session:', error);
      throw new Error('Failed to create portal session');
    }

    return data.url;
  }

  /**
   * Get user's subscription status
   */
  static async getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('subscription_status, subscription_expiry, tier, subscription_id, stripe_customer_id')
      .eq('id', userId)
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching subscription status:', error);
      return { isActive: false, status: 'inactive' };
    }

    // ✅ NEW: Check if subscription has expired and automatically downgrade
    const isExpired = profile.subscription_expiry && 
                     new Date(profile.subscription_expiry) <= new Date() &&
                     profile.tier === 'premium';

    if (isExpired) {
      // eslint-disable-next-line no-console
      console.log(`Subscription expired for user ${userId}, automatically downgrading to free tier`);
      
      // Downgrade user to free tier
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          tier: 'free',
          subscription_status: 'expired',
          subscription_expiry: null,
        })
        .eq('id', userId);

      if (updateError) {
        // eslint-disable-next-line no-console
        console.error('Error downgrading expired user:', updateError);
      } else {
        // eslint-disable-next-line no-console
        console.log(`Successfully downgraded user ${userId} to free tier`);
        // Update profile to reflect the downgrade
        profile.tier = 'free';
        profile.subscription_status = 'expired';
        profile.subscription_expiry = null;
      }
    }

    // Check if subscription is currently active (even if set to cancel)
    const isCurrentlyActive = profile.tier === 'premium' && 
                             ['active', 'trialing'].includes(profile.subscription_status) &&
                             (!profile.subscription_expiry || new Date(profile.subscription_expiry) > new Date());

    // Detect if subscription is set to cancel at period end
    // When cancel_at_period_end is true, webhook sets subscription_expiry to period end date
    // So if status is 'active' AND there's an expiry date in the future, it means cancellation is scheduled
    const cancelAtPeriodEnd = profile.subscription_status === 'active' && 
                             profile.subscription_expiry && 
                             new Date(profile.subscription_expiry) > new Date();

    return {
      isActive: isCurrentlyActive,
      status: profile.subscription_status || 'inactive',
      currentPeriodEnd: profile.subscription_expiry ? new Date(profile.subscription_expiry) : undefined,
      cancelAtPeriodEnd
    };
  }

  /**
   * Cancel subscription at period end
   */
  static async cancelSubscription(userId: string): Promise<boolean> {
    const { data, error } = await supabase.functions.invoke('cancel-subscription', {
      body: { userId }
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error canceling subscription:', error);
      return false;
    }

    return data.success;
  }
}

// Convenience function for upgrading to premium
export async function upgradeToPremmium(user: { uid: string; email: string | null }) {
  try {
    const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;
    if (!priceId) {
      throw new Error('Stripe price ID not configured');
    }

    const checkoutUrl = await StripeService.createCheckoutSession({
      priceId,
      userId: user.uid,
      userEmail: user.email || '',
      successUrl: `${window.location.origin}/?upgrade=success`,
      cancelUrl: `${window.location.origin}/?upgrade=canceled`
    });

    // Redirect to Stripe Checkout
    window.location.href = checkoutUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error upgrading to premium:', error);
    throw error;
  }
}
